import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { Button } from '../components/Button/Button';
import { GalleryCoverArt } from './GalleryCoverArt';
import { RemixButton } from './RemixButton';
import { ReportDialog } from './ReportDialog';
import { flattenInfinite, useGalleryApps, useGalleryList } from './useGallery';
import type { GalleryEntry, GalleryPublishedAppEntry } from './galleryApi';
import './GalleryPage.css';

/**
 * Public gallery — the showcase that hooks new visitors.
 *
 * Two lanes share this page:
 *
 *   1. **Remix kits** — approved `gallery_submissions`. Paginated
 *      with a "Show me more" button so a kid can scroll through
 *      thousands of entries without overloading the network.
 *   2. **Live apps preview** — the first 4 published mini-apps; a
 *      "See all live apps →" link goes to the dedicated `/gallery/apps`
 *      page where the live-app collection has its own pagination.
 *
 * Cards cross-link: when the same kid has both an approved submission
 * AND a live mini-app for the same project, each card surfaces a
 * mini-link to its sibling so a visitor never has to hunt for the
 * "other half" of the kid's work.
 */
const APP_PREVIEW_LIMIT = 4;

export function GalleryPage(): JSX.Element {
  const navigate = useNavigate();
  const submissions = useGalleryList();
  const apps = useGalleryApps(APP_PREVIEW_LIMIT);
  const [reporting, setReporting] = useState<{ id: string; name: string } | null>(null);

  const submissionsList = flattenInfinite<GalleryEntry>(submissions.data);
  const submissionsTotal = submissions.data?.pages[0]?.total ?? 0;
  const appsList = flattenInfinite<GalleryPublishedAppEntry>(apps.data);
  const appsTotal = apps.data?.pages[0]?.total ?? 0;

  const empty =
    !submissions.isPending &&
    !apps.isPending &&
    submissionsList.length === 0 &&
    appsList.length === 0;

  return (
    <div className="bl-page">
      <AppHeader
        subBrand="Gallery"
        privacy="published"
        back={{ label: 'Back to studio', to: '/studio' }}
      />
      <div className="bl-gallery">
        <section className="bl-gallery__hero">
          <h1 className="bl-gallery__title">Gallery</h1>
          <p className="bl-gallery__lead">
            Stuff kids made and shared with the world. Browse remixable model kits, then jump to the
            live apps section to try finished mini-apps in your browser.
          </p>
        </section>

        {(submissions.error || apps.error) && (
          <p className="bl-gallery__error" role="alert">
            {(submissions.error ?? apps.error)?.message}
          </p>
        )}

        {(submissions.isPending || apps.isPending) && (
          <p className="bl-gallery__status" aria-live="polite">
            Loading the gallery…
          </p>
        )}

        {empty && (
          <section className="bl-gallery__empty" aria-live="polite">
            <h2>Nothing here yet</h2>
            <p>
              Be the first! Train something cool in the studio and hit
              <strong> Publish to gallery</strong> to share it.
            </p>
            <Button onClick={() => navigate('/studio')}>Open the studio</Button>
          </section>
        )}

        {submissionsList.length > 0 && (
          <section className="bl-gallery__section" aria-labelledby="bl-gallery-remix-heading">
            <header className="bl-gallery__section-head">
              <div>
                <h2 id="bl-gallery-remix-heading">
                  Remix kits
                  <span className="bl-gallery__count">{submissionsTotal.toLocaleString()}</span>
                </h2>
                <p className="bl-gallery__section-sub">
                  Trained models you can fork and re-train with your own samples. Click a card for
                  the full story; tap Remix to start a new project from the same labels.
                </p>
              </div>
            </header>
            <ul className="bl-gallery__grid">
              {submissionsList.map((entry) => (
                <SubmissionTile
                  key={entry.id}
                  entry={entry}
                  onReport={() => setReporting({ id: entry.id, name: entry.project_name })}
                />
              ))}
            </ul>
            {submissions.hasNextPage && (
              <div className="bl-gallery__more">
                <button
                  type="button"
                  className="bl-gallery__more-btn"
                  onClick={() => void submissions.fetchNextPage()}
                  disabled={submissions.isFetchingNextPage}
                >
                  {submissions.isFetchingNextPage
                    ? 'Loading…'
                    : `Show me more (${(
                        submissionsTotal - submissionsList.length
                      ).toLocaleString()} left)`}
                </button>
              </div>
            )}
          </section>
        )}

        {appsList.length > 0 && (
          <section className="bl-gallery__section" aria-labelledby="bl-gallery-apps-heading">
            <header className="bl-gallery__section-head">
              <div>
                <h2 id="bl-gallery-apps-heading">
                  Live apps
                  <span className="bl-gallery__count">{appsTotal.toLocaleString()}</span>
                </h2>
                <p className="bl-gallery__section-sub">
                  Mini-apps kids deployed. Open them to try the live experience.{' '}
                  <strong>These can&rsquo;t be remixed</strong> — the creator shipped the finished
                  thing, not the dataset behind it.
                </p>
              </div>
              <Link to="/gallery/apps" className="bl-gallery__section-cta">
                See all live apps →
              </Link>
            </header>
            <ul className="bl-gallery__grid bl-gallery__grid--apps">
              {appsList.slice(0, APP_PREVIEW_LIMIT).map((app) => (
                <PublishedAppTile key={app.id} app={app} />
              ))}
            </ul>
            {appsTotal > APP_PREVIEW_LIMIT && (
              <div className="bl-gallery__more">
                <Link to="/gallery/apps" className="bl-gallery__more-btn">
                  Browse all {appsTotal.toLocaleString()} live apps →
                </Link>
              </div>
            )}
          </section>
        )}

        {reporting && (
          <ReportDialog
            isOpen={true}
            submissionId={reporting.id}
            projectName={reporting.name}
            onClose={() => setReporting(null)}
          />
        )}
      </div>
      <AppFooter />
    </div>
  );
}

function SubmissionTile({
  entry,
  onReport,
}: {
  entry: GalleryEntry;
  onReport: () => void;
}): JSX.Element {
  const labels = entry.class_labels;
  const labelLine =
    labels.length === 0
      ? 'classes hidden until approved'
      : labels.length <= 4
        ? labels.join(' · ')
        : `${labels.slice(0, 3).join(' · ')} +${labels.length - 3} more`;
  return (
    <li className="bl-gallery__card" data-kind={entry.project_kind}>
      <Link
        to={`/gallery/${entry.id}`}
        className="bl-gallery__card-cover"
        aria-label={`Open ${entry.project_name}`}
      >
        <GalleryCoverArt name={entry.project_name} kind={entry.project_kind} labels={labels} />
      </Link>
      <div className="bl-gallery__card-body">
        <Link to={`/gallery/${entry.id}`} className="bl-gallery__card-name">
          {entry.project_name}
        </Link>
        <p className="bl-gallery__card-kind">{entry.project_kind} · model</p>
        {entry.description && <p className="bl-gallery__card-desc">{entry.description}</p>}
        <p className="bl-gallery__card-classes" title={labels.join(', ')}>
          {labelLine}
        </p>
        <p className="bl-gallery__card-date">
          Shared {new Date(entry.submitted_at).toLocaleDateString()}
          {entry.remix_count > 0 && (
            <>
              {' · '}
              <span className="bl-gallery__card-remixes">
                Remixed {entry.remix_count}
                {entry.remix_count === 1 ? ' time' : ' times'}
              </span>
            </>
          )}
        </p>
        {entry.sibling_live_url && (
          <a
            href={entry.sibling_live_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bl-gallery__sibling"
            title="The same kid shipped a live mini-app for this project"
          >
            🚀 Try the live app
          </a>
        )}
        <div className="bl-gallery__card-actions">
          <RemixButton submissionId={entry.id} />
          <Link to={`/gallery/${entry.id}`} className="bl-gallery__card-detail">
            Detail →
          </Link>
          {!entry.is_mine && (
            <button
              type="button"
              className="bl-gallery__card-report"
              onClick={onReport}
              aria-label={`Report ${entry.project_name}`}
            >
              Report
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function PublishedAppTile({ app }: { app: GalleryPublishedAppEntry }): JSX.Element {
  return (
    <li className="bl-gallery__card bl-gallery__card--app">
      <a
        href={app.share_url}
        target="_blank"
        rel="noopener noreferrer"
        className="bl-gallery__card-cover"
        aria-label={`Open ${app.title} in a new tab`}
      >
        <GalleryCoverArt name={app.title} kind="app" labels={[]} />
      </a>
      <div className="bl-gallery__card-body">
        <a
          href={app.share_url}
          target="_blank"
          rel="noopener noreferrer"
          className="bl-gallery__card-name"
        >
          {app.title} <span aria-hidden="true">↗</span>
        </a>
        <p className="bl-gallery__card-kind">live mini-app · v{app.version}</p>
        <p className="bl-gallery__card-classes">{app.view_count.toLocaleString()} visits</p>
        <p className="bl-gallery__card-date">
          Shipped {new Date(app.published_at).toLocaleDateString()}
        </p>
        <p className="bl-gallery__card-no-remix">
          Not remixable — the live app doesn&rsquo;t share its dataset.
        </p>
        {app.sibling_submission_id && (
          <Link
            to={`/gallery/${app.sibling_submission_id}`}
            className="bl-gallery__sibling"
            title="The same kid published the underlying model — you can remix that"
          >
            🧩 Remix the underlying kit
          </Link>
        )}
        <div className="bl-gallery__card-actions">
          <a
            href={app.share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bl-gallery__card-open"
          >
            Open
          </a>
        </div>
      </div>
    </li>
  );
}
